import { KeyDeriver, PrivateKey } from "@bsv/sdk";
import { bapIdFromAddress } from "bsv-bap";
import {
	appendSeedProfile,
	deriveSeedProfile,
	type SeedBackup,
	validateSeedBackup,
} from "./seed-contract";

/** Operational authority for one peer; never retains a mnemonic or ancestor HD key. */
export class SeedProfileIdentity {
	readonly #accountWif: string;
	readonly #profile: ReturnType<typeof deriveSeedProfile>;

	constructor(profile: ReturnType<typeof deriveSeedProfile>) {
		const accountKey = PrivateKey.fromWif(profile.accountWif);
		const address = new KeyDeriver(accountKey)
			.derivePublicKey([1, "sigma"], "identity-0", "self", true)
			.toAddress();
		if (
			!Number.isInteger(profile.index) ||
			profile.index < 0 ||
			profile.index > 0x7f_ff_ff_ff ||
			profile.path !== `m/0'/${profile.index}'` ||
			profile.accountPublicKey !== accountKey.toPublicKey().toString() ||
			profile.identityAddress !== address ||
			profile.bapId !== bapIdFromAddress(address)
		) {
			throw new Error("Invalid selected seed profile");
		}
		this.#accountWif = profile.accountWif;
		this.#profile = { ...profile };
	}

	get bapId(): string {
		return this.#profile.bapId;
	}
	get rootAddress(): string {
		return this.#profile.identityAddress;
	}
	get rootPath(): string {
		return this.#profile.path;
	}
	get index(): number {
		return this.#profile.index;
	}

	getAccountKey(): PrivateKey {
		return PrivateKey.fromWif(this.#accountWif);
	}

	/** A path assertion, not a derivation API. Sibling and arbitrary paths fail. */
	getWalletRoot(profilePath: string): PrivateKey {
		if (profilePath !== this.rootPath) {
			throw new Error("Wallet root path must match the selected seed profile");
		}
		return this.getAccountKey();
	}

	getSigningPrivateKey(): PrivateKey {
		return new KeyDeriver(this.getAccountKey()).derivePrivateKey(
			[1, "sigma"],
			"identity-0",
			"self"
		);
	}

	getSigningPublicKey(): string {
		return this.getSigningPrivateKey().toPublicKey().toString();
	}

	getSigningAddress(): string {
		return this.getSigningPrivateKey().toAddress();
	}

	rotate(): never {
		throw new Error("Seed profile rotation is not supported in version 1");
	}
}

/**
 * Recovery authority. This is intentionally not a BAP/MasterID implementation:
 * legacy encrypted ids and Type42 root-path operations have different semantics.
 * Mutations are in memory; callers must persist exportBackup() atomically.
 */
export class SeedProfileAdapter {
	#backup: SeedBackup;

	constructor(backup: unknown) {
		this.#backup = structuredClone(validateSeedBackup(backup));
	}

	listIds(): string[] {
		return this.#backup.profiles.map((profile) => profile.bapId);
	}

	getId(bapId: string): SeedProfileIdentity | null {
		const profile = this.#backup.profiles.find(
			(entry) => entry.bapId === bapId
		);
		return profile
			? new SeedProfileIdentity(
					deriveSeedProfile(this.#backup.mnemonic, profile.index)
				)
			: null;
	}

	newId(): SeedProfileIdentity {
		const { backup, profile } = appendSeedProfile(this.#backup);
		const identity = new SeedProfileIdentity(profile);
		this.#backup = backup;
		return identity;
	}

	removeId(bapId: string): void {
		if (this.#backup.inventoryComplete === false) {
			throw new Error(
				"Import your complete backup file before removing a profile."
			);
		}
		const profiles = this.#backup.profiles.filter(
			(entry) => entry.bapId !== bapId
		);
		if (profiles.length === this.#backup.profiles.length) {
			return;
		}
		if (profiles.length === 0) {
			throw new Error("Cannot remove the final seed profile");
		}
		// Never rewind allocation: removed profile indexes must not be reused.
		this.#backup = { ...this.#backup, profiles };
	}

	exportBackup(): SeedBackup {
		return structuredClone(this.#backup);
	}
}
